import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import { colors, formatNumber } from '../../styles/tokens'
import { ErrorBanner, SkeletonRows, EmptyState } from '../../components/Feedback'
import EstimationBreadcrumb from '../../components/EstimationBreadcrumb'

// =========================================================================
// Budget Summary -- BUDGET-ONLY detailed breakdown.
//
// Single source of truth: build_costspace_export_payload(freeze_snapshot_id)
// for Frozen revisions -- the same RPC that already powers Direct Budget
// Review and the CostSpace export contract. No independent calculation is
// performed here; every number displayed is read verbatim from the
// payload's direct_budget[] / indirect_budget[] arrays. Grouping by Cost
// Type -> Package -> Activity, and the Total Budget sum, are pure
// presentation-level aggregation of numbers the payload already contains
// -- never a new formula.
//
// This page intentionally does NOT show Tender Price, Markup, or any
// Budget-to-Tender reconciliation. It answers exactly one question:
// "What is the Budget, and how is it built up?"
// =========================================================================

const COST_TYPE_LABEL = {
  normal_indirect: 'Normal Indirect',
  risk_contingency: 'Risk & Contingency',
  ho_overhead: 'HO Overhead',
}

export default function BudgetSummaryPage() {
  const { tenderId, revisionId } = useParams()
  const [revision, setRevision] = useState(null)
  const [tender, setTender] = useState(null)
  const [payload, setPayload] = useState(null)
  const [error, setError] = useState(null)
  const [expandedActivities, setExpandedActivities] = useState(new Set())

  useEffect(() => {
    let cancelled = false
    async function load() {
      setError(null)
      const revRes = await supabase.from('estimate_revision')
        .select('status, revision_number, label, tender_id')
        .eq('id', revisionId).maybeSingle()
      if (revRes.error) { if (!cancelled) setError(revRes.error.message); return }
      if (cancelled) return
      setRevision(revRes.data)

      if (revRes.data?.tender_id) {
        const tRes = await supabase.from('tender').select('name, client_name, currency').eq('id', revRes.data.tender_id).maybeSingle()
        if (!cancelled && !tRes.error) setTender(tRes.data)
      }

      if (revRes.data?.status === 'frozen') {
        // Frozen -- CORRECTED: build_costspace_export_payload() was
        // confirmed (directly, against a real frozen revision) to
        // return direct_budget: [] and indirect_budget: [] together --
        // it reports RECONCILIATION_MISMATCH internally because
        // rate_buildup_line detail was never captured in freeze_snapshot
        // (a separate, already-documented limitation), and it also
        // requires tender_costspace_project_mapping, which is empty by
        // design. That RPC is export-specific tooling, not a reliable
        // read source for this review screen. Read directly from the
        // immutable freeze_snapshot instead -- same split already
        // proven for BoqTab/TenderSummaryPage/RevisionComparisonPage/
        // DirectBudgetReviewPage: every value below is verbatim from
        // the snapshot, or the same simple presentation-level math this
        // page's own Draft path already performs (distSum, tender_budget
        // = amount ?? distSum, rate = budget / qty) -- no financial
        // recalculation, no second SSOT.
        const snapRes = await supabase.from('freeze_snapshot')
          .select('id, full_boq_data, full_indirect_data')
          .eq('estimate_revision_id', revisionId).maybeSingle()
        if (snapRes.error) { if (!cancelled) setError(snapRes.error.message); return }
        if (!snapRes.data) { if (!cancelled) setPayload(false); return }

        const boqData = Array.isArray(snapRes.data.full_boq_data) ? snapRes.data.full_boq_data : []
        const indirectData = snapRes.data.full_indirect_data && typeof snapRes.data.full_indirect_data === 'object' ? snapRes.data.full_indirect_data : {}
        const siteOverheadItems = Array.isArray(indirectData.site_overhead_items) ? indirectData.site_overhead_items : []
        const riskLines = Array.isArray(indirectData.risk_lines) ? indirectData.risk_lines : []
        const hoSetting = indirectData.ho_overhead_setting && typeof indirectData.ho_overhead_setting === 'object' ? indirectData.ho_overhead_setting : null
        // rate_buildup_lines carries resource_code/resource_description as of
        // the Batch D freeze_estimate_revision extension (LEFT JOIN
        // resource_library). Older snapshots simply won't have this key --
        // linesByBoqItem then resolves to an empty array per item, same as
        // before this change, never fabricated.
        const rateBuildupLines = Array.isArray(indirectData.rate_buildup_lines) ? indirectData.rate_buildup_lines : []
        const linesByBoqItem = {}
        for (const l of rateBuildupLines) {
          if (!l.boq_item_id) continue
          ;(linesByBoqItem[l.boq_item_id] ||= []).push(l)
        }

        const ccIds = [...new Set([
          ...boqData.map((it) => it.canonical_cost_code_id),
          ...siteOverheadItems.map((it) => it.canonical_cost_code_id),
          ...riskLines.map((it) => it.canonical_cost_code_id),
          hoSetting?.canonical_cost_code_id,
        ].filter(Boolean))]
        const { data: ccRows } = ccIds.length
          ? await supabase.from('canonical_cost_code').select('id, costspace_matched_code').in('id', ccIds)
          : { data: [] }
        const ccById = Object.fromEntries((ccRows || []).map((r) => [r.id, r.costspace_matched_code]))

        const direct = boqData.filter((it) => !it.is_excluded).map((it) => {
          const dc = Number(it.direct_cost) || 0
          const qty = Number(it.resolved_quantity) || 0
          return {
            boq_item_id: it.id, item_number: it.item_no,
            activity: {
              description: it.description, unit: it.unit, qty: it.resolved_quantity,
              unit_rate: qty !== 0 ? dc / qty : null, total_budget: dc,
              cost_code: it.canonical_cost_code_id ? (ccById[it.canonical_cost_code_id] ?? null) : null,
            },
            // Rate Build-Up line detail, captured verbatim in freeze_snapshot
            // as of Batch D (consumption_factor, selected_rate, line_cost,
            // resource_code/resource_description). Same field mapping as the
            // Draft path below -- no financial value is recomputed here.
            // row_type/unit are not part of the Batch D snapshot extension
            // (only resource_code/resource_description were added) -- left
            // undefined here rather than invented, matching this page's own
            // "never fabricate a value the snapshot doesn't actually carry"
            // rule used throughout.
            resources: (linesByBoqItem[it.id] || []).map((l) => ({
              row_type: undefined,
              resource_code: l.resource_code ?? null,
              resource_description: l.resource_description ?? null,
              qty: qty * (Number(l.consumption_factor) || 0),
              unit: undefined,
              unit_rate: l.selected_rate,
              total: l.line_cost ?? null,
            })),
          }
        })

        const indirect = []
        for (const ind of siteOverheadItems) {
          const dist = ind.planned_monthly_distribution || {}
          const distSum = Object.values(dist).reduce((s, v) => s + (Number(v) || 0), 0)
          indirect.push({
            line_type: 'normal_indirect', description: ind.description, qty: ind.qty, unit: ind.unit,
            indirect_budget_rate: ind.indirect_budget_rate,
            tender_budget: ind.cost_nature === 'fixed' ? ind.fixed_amount : distSum,
            package_name: ind.package_name, department: ind.department,
            cost_code: ind.canonical_cost_code_id ? (ccById[ind.canonical_cost_code_id] ?? null) : null,
            planned_monthly_distribution: dist,
          })
        }
        for (const r of riskLines) {
          const dist = r.planned_monthly_distribution || {}
          const distSum = Object.values(dist).reduce((s, v) => s + (Number(v) || 0), 0)
          indirect.push({
            line_type: 'risk_contingency', description: r.description, qty: 1, unit: 'LS',
            tender_budget: r.amount ?? distSum, package_name: r.package_name,
            cost_code: r.canonical_cost_code_id ? (ccById[r.canonical_cost_code_id] ?? null) : null,
            planned_monthly_distribution: dist,
          })
        }
        if (hoSetting) {
          const dist = hoSetting.planned_monthly_distribution || {}
          const distSum = Object.values(dist).reduce((s, v) => s + (Number(v) || 0), 0)
          indirect.push({
            line_type: 'ho_overhead', description: 'HO Overhead', qty: 1, unit: 'LS',
            tender_budget: distSum, package_name: hoSetting.package_name,
            cost_code: hoSetting.canonical_cost_code_id ? (ccById[hoSetting.canonical_cost_code_id] ?? null) : null,
            planned_monthly_distribution: dist,
          })
        }

        if (!cancelled) setPayload({ direct_budget: direct, indirect_budget: indirect })
        return
      }

      // Draft: build the identical shape from live SSOT tables -- the
      // same approach already used by Direct Budget Review's own Draft
      // path, so both pages agree on what "Budget" means before Freeze.
      // Zero calculation happens here: every value is a direct column
      // read, mirroring exactly what the RPC exposes after freezing.
      const [boqRes, dcRes, rateRes, indRes, riskRes, hoRes, ccRes] = await Promise.all([
        supabase.from('boq_item')
          .select('id, item_no, description, unit, resolved_quantity, is_excluded, canonical_cost_code_id')
          .eq('estimate_revision_id', revisionId).eq('is_excluded', false).order('item_no'),
        supabase.from('boq_item_direct_cost').select('boq_item_id, direct_cost'),
        supabase.from('boq_item_direct_unit_rate').select('boq_item_id, activity_unit_cost_rate'),
        supabase.from('indirect_cost_item').select('*').eq('estimate_revision_id', revisionId),
        supabase.from('risk_contingency_line').select('*').eq('estimate_revision_id', revisionId),
        supabase.from('ho_overhead_setting').select('*').eq('estimate_revision_id', revisionId).maybeSingle(),
        supabase.from('canonical_cost_code').select('id, code, costspace_matched_code'),
      ])
      if (boqRes.error) { if (!cancelled) setError(boqRes.error.message); return }
      const dcByItem = Object.fromEntries((dcRes.data || []).map((r) => [r.boq_item_id, r.direct_cost]))
      const rateByItem = Object.fromEntries((rateRes.data || []).map((r) => [r.boq_item_id, r.activity_unit_cost_rate]))
      const ccById = Object.fromEntries((ccRes.data || []).map((c) => [c.id, c.costspace_matched_code]))

      const direct = []
      for (const bi of boqRes.data || []) {
        const { data: lineRows } = await supabase
          .from('rate_buildup_line')
          .select('id, consumption_factor, selected_rate, resource_library(code, unit, resource_type, description)')
          .eq('boq_item_id', bi.id)
        const lineIds = (lineRows || []).map((l) => l.id)
        const { data: costRows } = lineIds.length
          ? await supabase.from('rate_buildup_line_cost').select('rate_buildup_line_id, line_cost').in('rate_buildup_line_id', lineIds)
          : { data: [] }
        const costByLineId = Object.fromEntries((costRows || []).map((c) => [c.rate_buildup_line_id, c.line_cost]))
        const resources = (lineRows || []).map((r) => ({
          row_type: r.resource_library?.resource_type,
          resource_code: r.resource_library?.code,
          resource_description: r.resource_library?.description,
          qty: (bi.resolved_quantity || 0) * (r.consumption_factor || 0),
          unit: r.resource_library?.unit,
          unit_rate: r.selected_rate,
          total: costByLineId[r.id] ?? null,
        }))
        direct.push({
          boq_item_id: bi.id, item_number: bi.item_no,
          activity: {
            description: bi.description, unit: bi.unit, qty: bi.resolved_quantity,
            unit_rate: rateByItem[bi.id] ?? null, total_budget: dcByItem[bi.id] ?? 0,
            cost_code: bi.canonical_cost_code_id ? (ccById[bi.canonical_cost_code_id] ?? null) : null,
          },
          resources,
        })
      }

      const indirect = []
      for (const ind of indRes.data || []) {
        const dist = ind.planned_monthly_distribution || {}
        const distSum = Object.values(dist).reduce((s, v) => s + (Number(v) || 0), 0)
        indirect.push({
          line_type: 'normal_indirect', description: ind.description, qty: ind.qty, unit: ind.unit,
          indirect_budget_rate: ind.indirect_budget_rate,
          tender_budget: ind.cost_nature === 'fixed' ? ind.fixed_amount : distSum,
          package_name: ind.package_name, department: ind.department,
          cost_code: ind.canonical_cost_code_id ? (ccById[ind.canonical_cost_code_id] ?? null) : null,
          planned_monthly_distribution: dist,
        })
      }
      for (const r of riskRes.data || []) {
        const dist = r.planned_monthly_distribution || {}
        const distSum = Object.values(dist).reduce((s, v) => s + (Number(v) || 0), 0)
        indirect.push({
          line_type: 'risk_contingency', description: r.description, qty: 1, unit: 'LS',
          tender_budget: r.amount ?? distSum, package_name: r.package_name,
          cost_code: r.canonical_cost_code_id ? (ccById[r.canonical_cost_code_id] ?? null) : null,
          planned_monthly_distribution: dist,
        })
      }
      if (hoRes.data) {
        const dist = hoRes.data.planned_monthly_distribution || {}
        const distSum = Object.values(dist).reduce((s, v) => s + (Number(v) || 0), 0)
        indirect.push({
          line_type: 'ho_overhead', description: 'Head Office Overhead', qty: 1, unit: 'LS',
          tender_budget: distSum, package_name: hoRes.data.package_name,
          cost_code: hoRes.data.canonical_cost_code_id ? (ccById[hoRes.data.canonical_cost_code_id] ?? null) : null,
          planned_monthly_distribution: dist,
        })
      }

      if (!cancelled) setPayload({ direct_budget: direct, indirect_budget: indirect })
    }
    load()
    return () => { cancelled = true }
  }, [revisionId])

  function toggleActivity(id) {
    setExpandedActivities((prev) => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  if (error) return <div style={{ padding: 24 }}><ErrorBanner message={error} /></div>
  if (revision === null) return <div style={{ padding: 24 }}><SkeletonRows rows={4} cols={2} /></div>
  if (payload === null) return <div style={{ padding: 24 }}><SkeletonRows rows={6} cols={3} /></div>
  if (payload === false) {
    return (
      <div style={{ padding: 24 }}>
        <EmptyState title="No frozen snapshot found" description="This Revision is marked frozen but no snapshot record could be found." />
      </div>
    )
  }

  const directBudget = payload.direct_budget || []
  const indirectBudget = payload.indirect_budget || []
  const totalDirect = directBudget.reduce((s, r) => s + (Number(r.activity?.total_budget) || 0), 0)
  const totalIndirect = indirectBudget.reduce((s, r) => s + (Number(r.tender_budget) || 0), 0)
  const totalBudget = totalDirect + totalIndirect

  // Group Indirect by Cost Type -> Package (pure client-side grouping of
  // the same rows above -- no recalculation).
  const byCostType = { normal_indirect: [], risk_contingency: [], ho_overhead: [] }
  indirectBudget.forEach((r) => { if (byCostType[r.line_type]) byCostType[r.line_type].push(r) })

  const isFrozen = revision.status === 'frozen'

  return (
    <div style={{ padding: 24, maxWidth: 900, margin: '0 auto' }}>
      <EstimationBreadcrumb />

      <div style={{ marginTop: 14, marginBottom: 24, display: 'flex', alignItems: 'center', gap: 10 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: colors.textPrimary, margin: 0 }}>Budget Summary</h1>
        <span style={{ fontSize: 12, fontWeight: 700, padding: '3px 10px', borderRadius: 6, background: isFrozen ? '#1E293B' : '#F1F5F9', color: isFrozen ? '#FFF' : colors.textSecondary }}>
          {isFrozen ? '🔒 FROZEN' : '📝 DRAFT'}
        </span>
      </div>

      <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 10, padding: 20, marginBottom: 20 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, fontSize: 13 }}>
          <div><span style={{ color: colors.textSecondary }}>Tender</span><div style={{ fontWeight: 600 }}>{tender?.name || '—'}</div></div>
          <div><span style={{ color: colors.textSecondary }}>Client</span><div style={{ fontWeight: 600 }}>{tender?.client_name || '—'}</div></div>
          <div><span style={{ color: colors.textSecondary }}>Revision</span><div style={{ fontWeight: 600 }}>Rev {revision.revision_number} — {revision.label}</div></div>
        </div>
      </div>

      {/* TOTAL BUDGET -- hero. Direct + Indirect ONLY. No Tender Price. */}
      <div style={{ background: '#0F1017', borderRadius: 10, padding: '28px 24px', textAlign: 'center', marginBottom: 20 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: 'rgba(255,255,255,.6)', textTransform: 'uppercase', letterSpacing: 0.6 }}>
          Total Budget
        </div>
        <div style={{ fontSize: 36, fontWeight: 800, color: '#FFFFFF', marginTop: 6 }}>
          {formatNumber(totalBudget)} {tender?.currency || ''}
        </div>
        <div style={{ fontSize: 12, color: 'rgba(255,255,255,.5)', marginTop: 8 }}>
          Direct {formatNumber(totalDirect)} + Indirect {formatNumber(totalIndirect)}
        </div>
      </div>

      {/* DIRECT BUDGET */}
      <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 10, overflow: 'hidden', marginBottom: 20 }}>
        <div style={{ padding: '12px 20px', background: '#FAFBFC', borderBottom: `1px solid ${colors.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: 0.4 }}>Direct Budget</span>
          <span style={{ fontSize: 14, fontWeight: 700, color: colors.textPrimary }}>{formatNumber(totalDirect)} {tender?.currency || ''}</span>
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#FAFBFC' }}>
              <Th></Th><Th>Activity</Th><Th align="right">Qty</Th><Th>Unit</Th><Th align="right">Rate</Th><Th align="right">Total Budget</Th>
            </tr>
          </thead>
          <tbody>
            {directBudget.map((row, i) => {
              const key = row.boq_item_id || `d${i}`
              const isOpen = expandedActivities.has(key)
              return (
                <>
                  <tr key={key} style={{ borderTop: `1px solid ${colors.border}` }}>
                    <Td>
                      {row.resources?.length > 0 && (
                        <span style={{ cursor: 'pointer' }} onClick={() => toggleActivity(key)}>{isOpen ? '▾' : '▸'}</span>
                      )}
                    </Td>
                    <Td>{row.item_number ? `${row.item_number} — ` : ''}{row.activity?.description}</Td>
                    <Td align="right">{formatNumber(row.activity?.qty)}</Td>
                    <Td muted>{row.activity?.unit}</Td>
                    <Td align="right">{formatNumber(row.activity?.unit_rate)}</Td>
                    <Td align="right" strong>{formatNumber(row.activity?.total_budget)}</Td>
                  </tr>
                  {isOpen && (row.resources || []).map((res, j) => (
                    <tr key={`${key}r${j}`} style={{ background: '#FAFBFC', borderTop: `1px solid ${colors.border}` }}>
                      <Td></Td>
                      <Td style={{ paddingLeft: 24, fontWeight: 600, textTransform: 'capitalize' }}>{res.row_type || '—'} — {res.resource_description || res.resource_code || '—'}</Td>
                      <Td align="right">{formatNumber(res.qty)}</Td>
                      <Td muted>{res.unit || '—'}</Td>
                      <Td align="right">{formatNumber(res.unit_rate)}</Td>
                      <Td align="right">{formatNumber(res.total)}</Td>
                    </tr>
                  ))}
                </>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* INDIRECT BUDGET -- Cost Type -> Package -> Activity */}
      <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 10, overflow: 'hidden', marginBottom: 20 }}>
        <div style={{ padding: '12px 20px', background: '#FAFBFC', borderBottom: `1px solid ${colors.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: 0.4 }}>Indirect Budget</span>
          <span style={{ fontSize: 14, fontWeight: 700, color: colors.textPrimary }}>{formatNumber(totalIndirect)} {tender?.currency || ''}</span>
        </div>
        {['normal_indirect', 'risk_contingency', 'ho_overhead'].map((costType) => {
          const rows = byCostType[costType]
          if (!rows.length) return null
          const costTypeTotal = rows.reduce((s, r) => s + (Number(r.tender_budget) || 0), 0)
          // Group by Package within this Cost Type -- pure grouping,
          // package_name comes verbatim from each row.
          const byPackage = {}
          rows.forEach((r) => {
            const pkg = r.package_name || '(Unassigned)'
            if (!byPackage[pkg]) byPackage[pkg] = []
            byPackage[pkg].push(r)
          })
          return (
            <div key={costType} style={{ borderTop: `1px solid ${colors.border}` }}>
              <div style={{ padding: '9px 20px', background: '#F3F4F6', fontSize: 12, fontWeight: 700, color: colors.textSecondary, display: 'flex', justifyContent: 'space-between' }}>
                <span>{COST_TYPE_LABEL[costType]}</span>
                <span>{formatNumber(costTypeTotal)} {tender?.currency || ''}</span>
              </div>
              {Object.entries(byPackage).map(([pkgName, pkgRows]) => {
                const pkgTotal = pkgRows.reduce((s, r) => s + (Number(r.tender_budget) || 0), 0)
                return (
                  <div key={pkgName}>
                    <div style={{ padding: '7px 20px 7px 32px', fontSize: 12, fontWeight: 600, color: colors.textSecondary, display: 'flex', justifyContent: 'space-between' }}>
                      <span>{pkgName}</span>
                      <span>{formatNumber(pkgTotal)}</span>
                    </div>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                      <thead>
                        <tr>
                          <Th style={{ paddingLeft: 44 }}>Activity</Th><Th align="right">Qty</Th><Th>Unit</Th><Th align="right">Rate</Th><Th>Cost Code</Th><Th align="right">Budget</Th>
                        </tr>
                      </thead>
                      <tbody>
                        {pkgRows.map((r, i) => {
                          const dist = r.planned_monthly_distribution || {}
                          const months = Object.keys(dist)
                          const isRiskOrHo = costType !== 'normal_indirect'
                          const qty = isRiskOrHo ? months.length : r.qty
                          const rate = isRiskOrHo ? (qty > 0 ? Number(r.tender_budget) / qty : null) : r.indirect_budget_rate
                          const unit = isRiskOrHo ? 'month' : r.unit
                          return (
                            <tr key={i} style={{ borderTop: `1px solid ${colors.border}` }}>
                              <Td style={{ paddingLeft: 44 }}>{r.description}</Td>
                              <Td align="right">{formatNumber(qty)}</Td>
                              <Td muted>{unit || '—'}</Td>
                              <Td align="right">{formatNumber(rate)}</Td>
                              <Td muted>{r.cost_code || '—'}</Td>
                              <Td align="right" strong>{formatNumber(r.tender_budget)}</Td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                )
              })}
            </div>
          )
        })}
      </div>
    </div>
  )
}

function Th({ children, align = 'left', style }) {
  return <th style={{ textAlign: align, padding: '8px 20px', fontSize: 11, fontWeight: 600, color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: 0.4, ...style }}>{children}</th>
}
function Td({ children, align = 'left', muted, strong, style }) {
  return <td style={{ textAlign: align, padding: '8px 20px', fontSize: 13, fontWeight: strong ? 700 : 400, color: muted ? colors.textMuted : colors.textPrimary, ...style }}>{children}</td>
}
